import { IonButton, IonIcon, IonLabel } from "@ionic/react";
import { swapHorizontalOutline } from "ionicons/icons";
import { FC, useEffect, useState } from "react";
import { useCommon } from "../../../common/useCommon";
import { Difficulty, getDifficultyLevel } from "../../../common/difficulty";
import { DifficultyDots } from "../../../components/DifficultyDots/DifficultyDots";
import { Flex } from "../../../components/Flex/Flex";
import { IPicture } from "../../../models";
import { useCategories } from "../../Pictures/useCategories";
import { usePictures } from "../../Pictures/usePictures";

interface InfoEditProps {
  pic: IPicture;
}

export const InfoEdit: FC<InfoEditProps> = ({ pic }) => {
  const { darkMode } = useCommon();
  const { updatePictureInfo } = usePictures();
  const { categoriesAdmin } = useCategories();

  const inputStyle = {
    backgroundColor: !darkMode
      ? "var(--ion-color-dark)"
      : "var(--ion-color-light)",
    border: "2px solid #8888",
    color: darkMode ? "var(--ion-color-dark)" : "var(--ion-color-light)",
    lineHeight: "10px",
    margin: 5,
    outline: "none",
    padding: 3,
  };

  const picTitle = pic.title || "";
  const picSizeX = String(pic.sizeX ?? "");
  const picSizeY = String(pic.sizeY ?? "");
  const picCategory = String(pic.category ?? "");
  const picDifficulty = pic.difficulty ?? null;

  const [title, setTitle] = useState<string>(picTitle);
  const [sizeX, setSizeX] = useState<string>(picSizeX);
  const [sizeY, setSizeY] = useState<string>(picSizeY);
  const [category, setCategory] = useState<string>(picCategory);
  const [difficulty, setDifficulty] = useState<Difficulty | null>(
    picDifficulty
  );
  const [saving, setSaving] = useState<boolean>(false);

  // Re-seed from the store, so a reload of allPicturesAdmin cannot leave stale
  // local values behind.
  useEffect(() => {
    setTitle(picTitle);
    setSizeX(picSizeX);
    setSizeY(picSizeY);
    setCategory(picCategory);
    setDifficulty(picDifficulty);
  }, [pic.id, picTitle, picSizeX, picSizeY, picCategory, picDifficulty]);

  // Computed, never sticky - typing the original value back hides the buttons.
  const hasChanges =
    title.trim() !== picTitle ||
    sizeX !== picSizeX ||
    sizeY !== picSizeY ||
    category !== picCategory ||
    difficulty !== picDifficulty;

  const isValid =
    !!title.trim() &&
    Number(sizeX) > 0 &&
    Number(sizeY) > 0 &&
    Number(category) > 0;

  const cancelEdit = () => {
    setTitle(picTitle);
    setSizeX(picSizeX);
    setSizeY(picSizeY);
    setCategory(picCategory);
    setDifficulty(picDifficulty);
  };

  const swapSizes = () => {
    setSizeX(sizeY);
    setSizeY(sizeX);
  };

  const save = async () => {
    setSaving(true);
    await updatePictureInfo(
      pic.id,
      title.trim(),
      Number(sizeX),
      Number(sizeY),
      Number(category),
      difficulty
    );
    setSaving(false);
  };

  return (
    <div style={{ padding: "0 5px 8px" }}>
      <IonLabel style={{ fontSize: 12, marginLeft: 5 }}>Naziv</IonLabel>
      <br />
      <input
        type="text"
        maxLength={100}
        value={title}
        onChange={(e: any) => setTitle(e.target.value)}
        style={{ ...inputStyle, width: "calc(100% - 14px)" }}
      />

      <Flex css={{ alignItems: "flex-end" }}>
        <div>
          <IonLabel style={{ fontSize: 12, marginLeft: 5 }}>Širina</IonLabel>
          <br />
          <input
            type="number"
            value={sizeX}
            onChange={(e: any) => setSizeX(e.target.value)}
            style={{ ...inputStyle, width: 60, textAlign: "center" }}
          />
        </div>
        <IonButton
          size="small"
          fill="clear"
          title="Zameni širinu i visinu"
          onClick={swapSizes}
          style={{ marginBottom: 2 }}
        >
          <IonIcon icon={swapHorizontalOutline} />
        </IonButton>
        <div>
          <IonLabel style={{ fontSize: 12, marginLeft: 5 }}>Visina</IonLabel>
          <br />
          <input
            type="number"
            value={sizeY}
            onChange={(e: any) => setSizeY(e.target.value)}
            style={{ ...inputStyle, width: 60, textAlign: "center" }}
          />
        </div>
      </Flex>

      <IonLabel style={{ fontSize: 12, marginLeft: 5 }}>Kategorija</IonLabel>
      <br />
      <select
        value={category}
        onChange={(e: any) => setCategory(e.target.value)}
        style={{ ...inputStyle, width: "calc(100% - 14px)" }}
      >
        <option value="" disabled>
          -
        </option>
        {categoriesAdmin.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>

      <IonLabel style={{ fontSize: 12, marginLeft: 5 }}>Nivo težine</IonLabel>
      <Flex css={{ alignItems: "center", gap: 6, margin: "0 3px" }}>
        <DifficultyDots value={difficulty} onChange={setDifficulty} size={22} />
        <IonLabel style={{ fontSize: 13 }}>
          {getDifficultyLevel(difficulty)?.label ?? "–"}
        </IonLabel>
      </Flex>

      {hasChanges && (
        <Flex css={{ justifyContent: "space-evenly", marginTop: 5 }}>
          <IonButton
            color={"medium"}
            onClick={cancelEdit}
            style={{ width: "45%" }}
          >
            Otkaži
          </IonButton>
          <IonButton
            color={"success"}
            disabled={saving || !isValid}
            onClick={save}
            style={{ width: "45%" }}
          >
            Snimi
          </IonButton>
        </Flex>
      )}
    </div>
  );
};
